import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ArrowLeft, Save, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

export default function QuoteEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [clientId, setClientId] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0, unit: 'unidad', catalog_item_id: null }]);
  const [validityDays, setValidityDays] = useState(15);
  const [taxRate, setTaxRate] = useState(0.16);

  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [c, cat, s] = await Promise.all([api.get('/clients'), api.get('/catalog'), api.get('/settings')]);
        setClients(c.data || []);
        setCatalog(cat.data || []);
        setSettings(s.data);
        if (!isEdit) {
          setValidityDays(s.data?.default_validity_days || 15);
          setTaxRate(s.data?.tax_rate ?? 0.16);
          if (s.data?.default_notes) setNotes(s.data.default_notes);
          setPaymentTerms(s.data?.default_payment_terms || '');
        }
        if (isEdit) {
          const q = await api.get(`/quotes/${id}`);
          setClientId(q.data.client_id);
          setTitle(q.data.title || '');
          setNotes(q.data.notes || '');
          setPaymentTerms(q.data.payment_terms || '');
          setItems(q.data.items.length ? q.data.items : [{ description: '', quantity: 1, unit_price: 0, unit: 'unidad' }]);
          setValidityDays(q.data.validity_days || 15);
          setTaxRate(q.data.tax_rate ?? 0.16);
        }
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const tax = subtotal * (Number(taxRate) || 0);
    return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
  }, [items, taxRate]);

  const addEmpty = () => setItems([...items, { description: '', quantity: 1, unit_price: 0, unit: 'unidad' }]);
  const addFromCatalog = (c) => {
    setItems([...items, { description: c.name + (c.description ? ` – ${c.description}` : ''), quantity: 1, unit_price: c.unit_price, unit: c.unit, catalog_item_id: c.id }]);
    setCatalogPickerOpen(false);
    setCatalogQuery('');
  };
  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: field === 'quantity' || field === 'unit_price' ? value : value };
    setItems(next);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = async () => {
    if (!clientId) { toast.error('Selecciona un cliente'); return; }
    if (items.length === 0 || !items.some((it) => it.description.trim())) { toast.error('Agrega al menos un ítem'); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        title,
        notes,
        payment_terms: paymentTerms,
        items: items.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unit_price: parseFloat(it.unit_price) || 0,
          unit: it.unit || 'unidad',
          catalog_item_id: it.catalog_item_id || null,
        })).filter((it) => it.description.trim()),
        validity_days: parseInt(validityDays) || 15,
        tax_rate: parseFloat(taxRate) || 0,
      };
      let resp;
      if (isEdit) resp = await api.put(`/quotes/${id}`, payload);
      else resp = await api.post('/quotes', payload);
      toast.success(isEdit ? 'Presupuesto actualizado' : 'Presupuesto creado');
      const newId = resp.data.id;
      // Ensure navigation runs after current render cycle (avoid React 19 race)
      setTimeout(() => navigate(`/quotes/${newId}`, { replace: true }), 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando…</div>;

  const filteredCatalog = catalog.filter((c) => (c.name + ' ' + (c.description || '') + ' ' + (c.category || '')).toLowerCase().includes(catalogQuery.toLowerCase()));

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Editar presupuesto' : 'Nuevo presupuesto'}
        subtitle={isEdit ? 'Modifica los datos y guarda.' : 'Selecciona el cliente, agrega ítems y guarda.'}
        actions={
          <div className="flex gap-2">
            <Link to="/quotes"><Button variant="ghost"><ArrowLeft className="mr-1.5 h-4 w-4" /> Volver</Button></Link>
            <Button className="btn-primary" onClick={save} disabled={saving} data-testid="quote-editor-save-button">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
              {isEdit ? 'Guardar cambios' : 'Crear presupuesto'}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Cliente</Label>
                  <Select value={clientId} onValueChange={setClientId}>
                    <SelectTrigger data-testid="quote-client-select">
                      <SelectValue placeholder="Selecciona un cliente" />
                    </SelectTrigger>
                    <SelectContent className="glass border-[var(--glass-border)] bg-[#0F1626]">
                      {clients.length === 0 ? (
                        <div className="p-3 text-xs text-muted-foreground">No hay clientes. <Link to="/clients" className="text-[hsl(var(--primary))]">Crear</Link></div>
                      ) : clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}{c.rif ? ` (${c.rif})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="title">Título / Asunto (opcional)</Label>
                  <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Servicios IT Q1" data-testid="quote-title-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Vigencia (días)</Label>
                  <Input type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} data-testid="quote-validity-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>IVA (%)</Label>
                  <Input type="number" min="0" step="0.01" value={(taxRate * 100).toFixed(2)} onChange={(e) => setTaxRate((parseFloat(e.target.value) || 0) / 100)} data-testid="quote-tax-input" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-semibold">Líneas del presupuesto</h3>
                <div className="flex gap-2">
                  <Dialog open={catalogPickerOpen} onOpenChange={setCatalogPickerOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="quote-add-from-catalog"><Search className="mr-1.5 h-4 w-4" /> Del catálogo</Button>
                    </DialogTrigger>
                    <DialogContent className="glass border-[var(--glass-border)] bg-[#0F1626] sm:max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Buscar en catálogo</DialogTitle>
                      </DialogHeader>
                      <Input autoFocus value={catalogQuery} onChange={(e) => setCatalogQuery(e.target.value)} placeholder="Buscar por nombre, categoría…" data-testid="quote-catalog-search" />
                      <div className="scrollbar-thin max-h-80 overflow-y-auto">
                        {filteredCatalog.length === 0 ? (
                          <div className="p-6 text-center text-sm text-muted-foreground">Sin resultados.</div>
                        ) : filteredCatalog.map((c) => (
                          <button key={c.id} type="button" onClick={() => addFromCatalog(c)} className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-[var(--surface-2)]" data-testid={`quote-catalog-pick-${c.name}`}>
                            <div className="min-w-0">
                              <div className="truncate font-medium">{c.name}</div>
                              <div className="truncate text-xs text-muted-foreground">{c.description || c.category}</div>
                            </div>
                            <div className="font-mono text-sm tabular-nums">{fmtMoney(c.unit_price)}</div>
                          </button>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button variant="outline" size="sm" onClick={addEmpty} data-testid="quote-add-empty-item"><Plus className="mr-1.5 h-4 w-4" /> Ítem libre</Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 text-left">Descripción</th>
                      <th className="px-2 py-2 text-right">Cant.</th>
                      <th className="px-2 py-2 text-left">Unidad</th>
                      <th className="px-2 py-2 text-right">Precio Unit.</th>
                      <th className="px-2 py-2 text-right">Total</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                      return (
                        <tr key={idx} className="border-t border-[var(--glass-border)]">
                          <td className="px-2 py-2">
                            <Textarea rows={1} value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Descripción del producto / servicio" data-testid={`quote-item-description-${idx}`} className="min-h-9 resize-none" />
                          </td>
                          <td className="px-2 py-2 align-top">
                            <Input type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="w-24 text-right font-mono" data-testid={`quote-item-qty-${idx}`} />
                          </td>
                          <td className="px-2 py-2 align-top">
                            <Input value={it.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} className="w-24" />
                          </td>
                          <td className="px-2 py-2 align-top">
                            <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} className="w-32 text-right font-mono" data-testid={`quote-item-price-${idx}`} />
                          </td>
                          <td className="px-2 py-2 text-right align-middle font-mono tabular-nums">{fmtMoney(line)}</td>
                          <td className="px-2 py-2 align-middle text-right">
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="text-[#FF8FA3] hover:text-[#FF5C7A]" data-testid={`quote-item-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="glass border-[var(--glass-border)]">
            <CardContent className="p-5">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Términos, condiciones, instrucciones de pago…" className="mt-1.5" data-testid="quote-notes-input" />
            </CardContent>
          </Card>

          <Card className="glass border-[rgba(85,117,245,0.30)] bg-[rgba(85,117,245,0.04)]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="payment_terms" className="text-[hsl(var(--primary))]">Condiciones de pago</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => setPaymentTerms(settings?.default_payment_terms || '')}
                  title="Restaurar al texto por defecto configurado en Settings"
                >
                  Usar predeterminado
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Aparece en el PDF y la página pública de verificación. Edita para esta cotización en particular.</p>
              <Textarea id="payment_terms" rows={3} value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="Ej: Precios en USD pagaderos a la tasa BCV. 50% al confirmar, 50% al entregar…" className="mt-2" data-testid="quote-payment-terms-input" />
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-20">
            <Card className="glass border-[var(--glass-border)]">
              <CardContent className="space-y-3 p-5">
                <h3 className="font-display text-base font-semibold">Resumen</h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono tabular-nums" data-testid="quote-summary-subtotal">{fmtMoney(totals.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">IVA ({(taxRate * 100).toFixed(0)}%)</span>
                  <span className="font-mono tabular-nums" data-testid="quote-summary-tax">{fmtMoney(totals.tax)}</span>
                </div>
                <div className="divider-accent my-1" />
                <div className="flex items-center justify-between">
                  <span className="font-display text-base font-semibold">TOTAL</span>
                  <span className="font-mono text-xl font-semibold tabular-nums text-[hsl(var(--primary))]" data-testid="quote-summary-total">{fmtMoney(totals.total)}</span>
                </div>
                <div className="mt-3 rounded-lg bg-[var(--surface-1)] p-3 text-xs text-muted-foreground">
                  Válido hasta: <span className="font-mono text-foreground">{fmtDate(new Date(Date.now() + (parseInt(validityDays) || 15) * 86400 * 1000))}</span>
                </div>
                <Button className="btn-primary mt-3 w-full" onClick={save} disabled={saving} data-testid="quote-summary-save-button">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                  {isEdit ? 'Guardar cambios' : 'Crear presupuesto'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
